import Kbd from '@/components/Kbd';
import { FiChevronDown } from '@/components/react-icons/main';
import Spinner from '@/components/Spinner';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import IconV2 from '@/components/v2/IconV2';
import useResponsive from '@/hooks/useResponsive';
import setPath from '@/lib/setPath';
import { cleanClass } from '@shared/utils/cleanClass.js';
import { randomId } from '@shared/utils/randomId.js';
import { forwardRef } from 'react';

const Button = forwardRef(({ children, className = '', style = {}, size = 'md', disabled, icon, iconPosition = 'left', iconProps, title, loading, route, target = '_self', replace = true, keepSearch = false, options = [], actions = [], value, onOptionClick, onClick, kbd, stopPropagation = true, dark = false, shrinkOnSmallScreen = false, ...rest }, ref) => {
  const id = `comp-button-${randomId()}`;
  const { mobileSmall } = useResponsive();

  const handleClick = (e) => {
    if (stopPropagation) {
      e.stopPropagation();
    }

    if (e.button === 2 || disabled || e.target.classList.contains('options-dropdown') || e.target?.nodeName === 'SVG') {
      e.preventDefault();
      return;
    }

    if (route) {
      if (route?.startsWith('http')) {
        const nextTarget = target === '_self' ? '_blank' : target;
        window.open(route, nextTarget, nextTarget === '_blank' ? 'noopener,noreferrer' : undefined);
        return;
      }
      setPath(route, { event: e, keepSearch, replace, target });
    }

    onClick?.(e);
  };

  const handleActionClick = (e, n) => {
    e.stopPropagation();
    e.preventDefault();
    n?.onClick?.(e);
  };

  const shouldShowChildren = shrinkOnSmallScreen ? !mobileSmall : true;

  return (
    <button
      ref={ref}
      className={cleanClass(`${id} comp-button ${className} size-${size} ${disabled ? 'disabled' : ''} ${options?.length || actions?.length ? 'has-options' : ''} ${!children ? 'no-text' : ''}`)} //
      style={style}
      disabled={disabled}
      data-title={title}
      onClick={handleClick}
      {...rest}>
      <div className={'button-main'}>
        {!!icon && iconPosition === 'left' && !loading && <IconV2 icon={icon} {...(iconProps || {})} />}
        {loading && <Spinner color={className?.includes('primary') ? '#FFF' : null} />}
        {shouldShowChildren && children}
        {!!icon && iconPosition === 'right' && !loading && options?.length === 0 && <IconV2 icon={icon} {...(iconProps || {})} />}
        {kbd && <Kbd className={className?.includes('primary') ? 'primary' : ''}>{kbd}</Kbd>}
      </div>
      {options?.length > 0 && (
        <div className={`${id}-options options-dropdown`}>
          <IconV2 icon={FiChevronDown} />
          {!disabled && <FloatingSelectV2 className={dark ? 'dark' : ''} triggers={[`.${id}-options`]} options={options} onChange={onOptionClick} useSearch={options?.length > 5} value={value} placement="bottom-end" offset={4} />}
        </div>
      )}
      {actions?.map((n, idx) => (
        <div key={idx} className={`options-dropdown`} onClick={(e) => handleActionClick(e, n)} title={n.title}>
          <IconV2 icon={n.icon} />
        </div>
      ))}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
